import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Typography} from '@customink/pigment-react';
import PdpTextField from './PdpTextField';
import PriceDetailsDialog from './PriceDetailsDialog';
import PriceDetailsToolTip from './PriceDetailsToolTip';
import PriceDetailsContent from './PriceDetailsContent';
import QuoteErrorMessage from './QuoteErrorMessage';
import {pricingOnPDP} from '../utils/constants/signalmanConstants';
import priceGridQuantities from '../utils/priceGridQuantities';
import DiscountMessage from './DiscountMessage';
import VolumeDiscountMessage from './VolumeDiscountMessage';
import usePriceQuote from '../hooks/queries/quotes';
import useDiscountPercentage from '../hooks/useDiscountPercentage';
import {useMobileBreakpoint} from '@customink/pigment-react/lib/utils/useBreakpoint';
import {debounce} from 'throttle-debounce';

export default function PdpPriceComponent({
  bulkQty,
  currentColor,
  defaultQty,
  name,
  minQty,
  hasSingles,
  productProps,
  specialty,
  handleMiniQuantityError
}) {
  const isMobile = useMobileBreakpoint();
  const getRightQuantity = () => Math.max(defaultQty, 25, minQty);
  const prevQuoteRef = useRef();
  const [inputQuoteQuantity, setInputQuoteQuantity] =
    useState(getRightQuantity());
  const singlesEnabled =
    productProps?.colors?.find(
      (color) => color?.legacyColorId === currentColor?.legacyColorId
    )?.singlesEnabled ?? false;

  const [miniumQuantityError, setMiniumQuantityError] = useState(false);
  const [quoteQuantity, setQuoteQuantity] = useState(getRightQuantity());
  const canOrderSingles = singlesEnabled ? minQty : bulkQty;
  const {
    data: quote,
    isError,
    isLoading
  } = usePriceQuote(currentColor?.legacyColorId, quoteQuantity, 1, 0, true);
  const getQuote = (!isLoading && quote && quote[quoteQuantity]) ?? [];

  const debounceTextInput = useCallback(
    debounce(700, () => {
      setQuoteQuantity(inputQuoteQuantity);
      setMiniumQuantityError(inputQuoteQuantity < canOrderSingles);
    }),
    [inputQuoteQuantity]
  );
  useEffect(() => {
    debounceTextInput();
    return () => debounceTextInput.cancel();
  }, [debounceTextInput]);

  const onChangeTextInput = (value) => {
    setInputQuoteQuantity(value);
  };

  const onBlurChange = (value) => {
    window.dataLayer.push({
      event: 'interaction',
      interaction_action: 'change field',
      interaction_category: 'pdp quantity',
      interaction_label: name,
      interaction_value: value
    });
  };


  useEffect(() => {
    if (getQuote) {
      prevQuoteRef.current = getQuote;
    }
  }, [getQuote]);

  const discountPercentage = useDiscountPercentage(
    getQuote,
    currentColor,
    canOrderSingles
  );

  const priceDetailQuotes = usePriceQuote(
    currentColor?.legacyColorId,
    priceGridQuantities(bulkQty, singlesEnabled, specialty),
    1,
    0
  );
  useEffect(() => {
    priceDetailQuotes?.refetch();
  }, [currentColor.legacyColorId]);
  const messageProps = {
    canOrderSingles,
    currentColor,
    defaultQty,
    discountPercentage,
    minQty,
    quoteQuantity
  };

  const discountMessage = <DiscountMessage {...messageProps} />;
  const volumeDiscountMessage = <VolumeDiscountMessage {...messageProps} />;
  const [messageToShow, setMessageToShow] = useState(null);

  useEffect(() => {
    if (typeof discountPercentage === 'number' && discountPercentage !== null) {
      setMessageToShow(
        discountPercentage < 5 ? discountMessage : volumeDiscountMessage
      );
    }
  }, [discountPercentage]);

  const getPrice = () => {
    if (isError || miniumQuantityError || isLoading) {
      return Array.isArray(prevQuoteRef.current) &&
        prevQuoteRef.current.length === 0
        ? '--'
        : prevQuoteRef.current;
    }
    return getQuote;
  };
  return (
    <>
      <Box className="pc-Style-Hero-pricing">
        <Box className="pc-Style-Hero-content">
          <Typography className="pc-Style-Hero-item-price">
            ${getPrice()} each
          </Typography>
          <Typography className="pc-Style-Hero-item-typography">for</Typography>
          <PdpTextField
            onHandleChange={onChangeTextInput}
            inputDefaultedValue={inputQuoteQuantity}
            onClick={() =>
              window.dataLayer.push({
                event: 'interaction',
                interaction_action: 'click field',
                interaction_category: 'pdp quantity',
                interaction_label: name
              })
            }
            onBlur={onBlurChange}
          />
          <Typography className="pc-Style-Hero-item-typography">
            items
          </Typography>
          {isMobile ? (
            <PriceDetailsDialog
              title={name}
              onOpenHandler={() => {
                window.dataLayer.push({
                  event: 'interaction',
                  interaction_action: 'pricing details',
                  interaction_category: 'product details page',
                  interaction_label: name
                });
              }}
              priceDetails={
                <PriceDetailsContent
                  priceDetailQuoteData={priceDetailQuotes?.data}
                />
              }
            />
          ) : (
            <PriceDetailsToolTip
              onOpenHandler={() => {
                window.dataLayer.push({
                  event: 'interaction',
                  interaction_action: 'pricing details',
                  interaction_category: 'product details page',
                  interaction_label: name
                });
              }}
              priceDetails={
                <PriceDetailsContent
                  priceDetailQuoteData={priceDetailQuotes?.data}
                />
              }
            />
          )}
        </Box>
        <Box className="pc-Style-Hero-error">
          {miniumQuantityError && (
            <QuoteErrorMessage
              canOrderSingles={canOrderSingles}
              hasSingles={hasSingles}
              handleMiniQuantityError={handleMiniQuantityError}
            />
          )}
          {!miniumQuantityError && messageToShow}
        </Box>
      </Box>
    </>
  );
}
